package dev.lingvo.shared.screenshots.text

/** Break policies distinguish dictionary-dependent scripts from space-delimited captions. */
enum class BreakingMode { SPACES, SEGMENTER, PHRASE_KINSOKU, KEEP_ALL }

/** One source of typography defaults prevents loading and fitting different script faces. */
data class ScriptRow(
    val locales: Set<String>, val sans: String, val serif: String, val display: String?,
    val minLineHeight: Double, val letterSpacingAllowed: Boolean, val breaking: BreakingMode,
    val rtl: Boolean = false,
)

/** Locale subtags take precedence over sample text so Han glyph forms stay region-correct. */
object Scripts : ScriptResolver {
    /** Exhaustive defaults are deliberately independent of platform font availability. */
    val rows: Map<Script, ScriptRow> = buildMap {
        fun row(script: Script, locales: String, sans: String, serif: String, display: String? = sans,
            lh: Double = 1.0, tracking: Boolean = true, mode: BreakingMode = BreakingMode.SPACES, rtl: Boolean = false) {
            put(script, ScriptRow(locales.split(' ').toSet(), sans, serif, display, lh, tracking, mode, rtl))
        }
        row(Script.LATIN, "af ca cs cy da de en es et eu fi fil fr ga gl hr hu id is it lt lv ms nb nl no pl pt ro sk sl sq sv sw tr az", "Noto Sans", "Noto Serif", null)
        row(Script.VIETNAMESE, "vi", "Be Vietnam Pro", "Noto Serif", lh = 1.15)
        row(Script.CYRILLIC, "bg mk ru sr uk", "Noto Sans", "PT Serif", "Inter Tight")
        row(Script.GREEK, "el", "Noto Sans", "Noto Serif", "Manrope", 1.1)
        row(Script.ARABIC, "ar fa ps sd ug ckb", "Noto Sans Arabic", "Noto Naskh Arabic", "Noto Kufi Arabic", 1.35, false, rtl = true)
        row(Script.URDU, "ur", "Noto Nastaliq Urdu", "Noto Nastaliq Urdu", "Noto Sans Arabic", 1.9, false, rtl = true)
        row(Script.HEBREW, "he iw yi", "Noto Sans Hebrew", "Noto Serif Hebrew", "Rubik", 1.15, false, rtl = true)
        row(Script.DEVANAGARI, "hi mr", "Noto Sans Devanagari", "Noto Serif Devanagari", "Mukta", 1.4, false)
        row(Script.BENGALI, "bn", "Noto Sans Bengali", "Noto Serif Bengali", "Hind Siliguri", 1.4, false)
        row(Script.GUJARATI, "gu", "Noto Sans Gujarati", "Noto Serif Gujarati", lh = 1.4, tracking = false)
        row(Script.GURMUKHI, "pa", "Noto Sans Gurmukhi", "Noto Serif Gurmukhi", lh = 1.4, tracking = false)
        row(Script.KANNADA, "kn", "Noto Sans Kannada", "Noto Serif Kannada", lh = 1.45, tracking = false)
        row(Script.MALAYALAM, "ml", "Noto Sans Malayalam", "Noto Serif Malayalam", lh = 1.45, tracking = false)
        row(Script.TAMIL, "ta", "Noto Sans Tamil", "Noto Serif Tamil", lh = 1.45, tracking = false)
        row(Script.TELUGU, "te", "Noto Sans Telugu", "Noto Serif Telugu", lh = 1.45, tracking = false)
        row(Script.THAI, "th", "Noto Sans Thai", "Noto Serif Thai", "Kanit", 1.45, false, BreakingMode.SEGMENTER)
        row(Script.KHMER, "km", "Noto Sans Khmer", "Noto Serif Khmer", "Kantumruy Pro", 1.6, false, BreakingMode.SEGMENTER)
        row(Script.MYANMAR, "my", "Noto Sans Myanmar", "Noto Serif Myanmar", "Padauk", 1.7, false, BreakingMode.SEGMENTER)
        row(Script.HANS, "zh", "Noto Sans SC", "Noto Serif SC", lh = 1.25, mode = BreakingMode.PHRASE_KINSOKU)
        row(Script.HANT, "zh-TW", "Noto Sans TC", "Noto Serif TC", lh = 1.25, mode = BreakingMode.PHRASE_KINSOKU)
        row(Script.HK, "zh-HK", "Noto Sans HK", "Noto Serif HK", lh = 1.25, mode = BreakingMode.PHRASE_KINSOKU)
        row(Script.JAPANESE, "ja", "Noto Sans JP", "Noto Serif JP", "Zen Kaku Gothic New", 1.3, mode = BreakingMode.PHRASE_KINSOKU)
        row(Script.KOREAN, "ko", "Noto Sans KR", "Noto Serif KR", "IBM Plex Sans KR", 1.25, mode = BreakingMode.KEEP_ALL)
    }

    /** Extension/private-use values cannot override the language's actual script and region. */
    override fun scriptFor(locale: String, sample: String?): Script {
        val subtags = locale.trim().replace('_', '-').lowercase().split('-')
        val parts = subtags.take(1) + subtags.drop(1).takeWhile { it.length != 1 }
        if ("latn" in parts) return Script.LATIN
        if (parts.first() == "zh") return when {
            "hk" in parts -> Script.HK
            "hant" in parts || "tw" in parts -> Script.HANT
            else -> Script.HANS
        }
        return rows.entries.firstOrNull { parts.first() in it.value.locales }?.key
            ?: scriptFromText(sample.orEmpty())
    }

    /** Allows callers without sample text to use the same locale contract. */
    fun scriptFor(locale: String): Script = scriptFor(locale, null)

    /** A refusal for an unsegmented dictionary script needs a different repair explanation. */
    fun needsDictionary(script: Script): Boolean = rows.getValue(script).breaking == BreakingMode.SEGMENTER

    /** Emoji is orthogonal to writing systems because the shared enum intentionally has no emoji script. */
    fun hasEmoji(text: String): Boolean = codePoints(text).any(::isEmojiCodePoint)

    /** Ignores common punctuation and surrogate halves when choosing an unknown locale's face. */
    fun scriptFromText(text: String): Script {
        val points = codePoints(text)
        if (points.any { it in 0x3040..0x30FF || it in 0x31F0..0x31FF }) return Script.JAPANESE
        return points.firstNotNullOfOrNull { point -> when (point) {
            in 0x0041..0x024F -> if (point.toChar().isLetter()) Script.LATIN else null
            in 0x0370..0x03FF -> Script.GREEK
            in 0x0400..0x052F -> Script.CYRILLIC
            in 0x0590..0x05FF -> Script.HEBREW
            in 0x0600..0x06FF, in 0x0750..0x077F, in 0x08A0..0x08FF -> Script.ARABIC
            in 0x0900..0x097F -> Script.DEVANAGARI
            in 0x0980..0x09FF -> Script.BENGALI
            in 0x0A00..0x0A7F -> Script.GURMUKHI
            in 0x0A80..0x0AFF -> Script.GUJARATI
            in 0x0B80..0x0BFF -> Script.TAMIL
            in 0x0C00..0x0C7F -> Script.TELUGU
            in 0x0C80..0x0CFF -> Script.KANNADA
            in 0x0D00..0x0D7F -> Script.MALAYALAM
            in 0x0E00..0x0E7F -> Script.THAI
            in 0x1000..0x109F, in 0xAA60..0xAA7F -> Script.MYANMAR
            in 0x1780..0x17FF -> Script.KHMER
            in 0x1100..0x11FF, in 0xAC00..0xD7AF -> Script.KOREAN
            in 0x3400..0x9FFF, in 0x20000..0x323AF -> Script.HANS
            else -> null
        } } ?: Script.LATIN
    }
}

internal fun codePoints(text: String): List<Int> = buildList {
    var i = 0
    while (i < text.length) {
        val first = text[i++].code
        add(if (first in 0xD800..0xDBFF && i < text.length && text[i].code in 0xDC00..0xDFFF) {
            0x10000 + (first - 0xD800) * 1024 + (text[i++].code - 0xDC00)
        } else first)
    }
}
